import React, { useRef, useState } from 'react';
import { TopAction } from '@/types/platform';
import { platformService } from '@/services/platformService';
import { extractErrorMessage } from '@/utils/fetchErrors';
import './TopActionPanel.css';

interface TopActionPanelProps {
  action: TopAction;
  getTranslation: (key: string) => string;
}

const TopActionPanel: React.FC<TopActionPanelProps> = ({ action, getTranslation }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [fileTypeError, setFileTypeError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fileComponent = action.panel.components.find(c => c.type === 'fileUpload');
  const processingMode = action.panel.processingModes[0];
  const acceptAttr = fileComponent?.accept?.join(',');

  const handleFileAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    if (fileComponent?.accept) {
      const ext = '.' + selected.name.split('.').pop()?.toLowerCase();
      if (!fileComponent.accept.includes(ext)) {
        setFileTypeError(`${getTranslation('upload.unsupportedFileType')} ${ext}`);
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
    }

    setFileTypeError(null);
    setFile(selected);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemove = () => {
    setFile(null);
    setFileTypeError(null);
  };

  const handleSubmit = async () => {
    if (!file) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const response = await platformService.uploadProductList(file);
      if (response.success) {
        setSubmitSuccess(true);
        setFile(null);
      } else {
        setSubmitError(response.errorMessage ?? getTranslation('topAction.error'));
      }
    } catch (err) {
      setSubmitError(extractErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setFile(null);
    setFileTypeError(null);
    setSubmitError(null);
    setSubmitSuccess(false);
  };

  return (
    <div className="top-action">
      <button
        type="button"
        className={`top-action-btn top-action-btn--${action.variant ?? 'secondary'}`}
        onClick={() => setIsOpen(prev => !prev)}
      >
        {getTranslation(action.labelKey)}
      </button>

      {isOpen && (
        <div className="top-action-panel">
          <div className="top-action-panel-header">
            <div>
              <div className="top-action-panel-title">{getTranslation(action.panel.titleKey)}</div>
              {action.panel.descriptionKey && (
                <div className="top-action-panel-description">{getTranslation(action.panel.descriptionKey)}</div>
              )}
            </div>
            <button className="top-action-panel-close" onClick={handleClose} aria-label="Close">✕</button>
          </div>

          {submitSuccess ? (
            <div className="top-action-success">
              <span className="top-action-success-icon">✓</span>
              {getTranslation('topAction.productList.successMessage')}
            </div>
          ) : (
            <>
              {fileComponent && (
                <div className="top-action-file-section">
                  <input
                    ref={fileInputRef}
                    type="file"
                    id={`top-action-${action.id}`}
                    accept={acceptAttr}
                    className="file-input-hidden"
                    onChange={handleFileAdd}
                  />
                  <button
                    type="button"
                    className="upload-button"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {getTranslation(fileComponent.labelKey)}
                  </button>

                  {fileTypeError && <p className="file-type-error">{fileTypeError}</p>}

                  {file && (
                    <div className="file-list">
                      <div className="file-item">
                        <span className="file-name">{file.name}</span>
                        <button
                          type="button"
                          className="file-remove-button"
                          onClick={handleRemove}
                          aria-label={getTranslation('upload.removeFile')}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {submitError && <div className="top-action-error">{submitError}</div>}

              <div className="top-action-panel-footer">
                <button
                  type="button"
                  className="submit-button"
                  disabled={!file || isSubmitting}
                  onClick={handleSubmit}
                >
                  {isSubmitting
                    ? getTranslation('form.submitting')
                    : getTranslation(processingMode.buttonLabelKey)}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TopActionPanel;
